/**
 * テーブルのQRコード用URLと、QR画像のダウンロード・印刷に関するユーティリティ。
 *
 * QR が指す先は「バックエンドの 302 リダイレクトエンドポイント」。
 * スキャンすると be が現在の稼働セッションを解決し、フロントの注文画面へ 302 で遷移する。
 * この出発URL（QRのURL）はサーバーリダイレクトの source となるため、ブラウザ履歴(Ctrl+H)の
 * 一覧に店舗/テーブルIDが残らない（feast-be ADR 0070 / feast-fe ADR 0023）。
 * ホストはバックエンドのベースURL（REACT_APP_API_BASE_URL）を使う。
 */

/** テーブルのQR用URL（be の redirect-to-order エンドポイント）を組み立てる。 */
export const buildTableQrUrl = (storeId: string, tableId: string): string =>
  `${process.env.REACT_APP_API_BASE_URL}/public/orders/stores/${storeId}/dining-tables/${tableId}/redirect-to-order`;

/** ファイル名に使えない文字を除去する。 */
const sanitizeFileName = (name: string): string =>
  name.replace(/[\\/:*?"<>|]/g, '_').trim() || 'table';

/** canvas を PNG としてダウンロードする。 */
export const downloadCanvasAsPng = (canvas: HTMLCanvasElement, name: string): void => {
  const dataUrl = canvas.toDataURL('image/png');
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `table-${sanitizeFileName(name)}-qr.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export interface PrintableQrItem {
  /** テーブル名（QR下に表示） */
  name: string;
  /** QR画像の data URL（canvas.toDataURL の結果） */
  dataUrl: string;
}

/**
 * 別ウィンドウを開いて QR 画像グリッドを印刷する。
 * 単体印刷・全テーブル一括印刷の両方で共用する。
 * アプリ本体の DOM / print CSS と干渉しないよう、独立した HTML ドキュメントを書き出す。
 */
export const openPrintWindow = (items: PrintableQrItem[]): void => {
  if (items.length === 0) return;

  const printWindow = window.open('', '_blank', 'width=900,height=700');
  if (!printWindow) {
    // ポップアップブロック時
    // eslint-disable-next-line no-alert
    alert('印刷ウィンドウを開けませんでした。ポップアップを許可してください。');
    return;
  }

  const escapeHtml = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const cards = items
    .map(
      (item) => `
        <div class="qr-card">
          <img src="${item.dataUrl}" alt="QR" />
          <div class="qr-name">${escapeHtml(item.name)}</div>
        </div>`
    )
    .join('');

  printWindow.document.write(`<!DOCTYPE html>
<html lang="ja">
  <head>
    <meta charset="utf-8" />
    <title>テーブルQRコード</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: sans-serif; margin: 16px; }
      .qr-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 16px;
      }
      .qr-card {
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        page-break-inside: avoid;
      }
      .qr-card img { width: 200px; height: 200px; }
      .qr-name { margin-top: 12px; font-size: 18px; font-weight: bold; text-align: center; }
      @media print {
        body { margin: 0; }
      }
    </style>
  </head>
  <body>
    <div class="qr-grid">${cards}</div>
    <script>
      window.onload = function () {
        window.focus();
        window.print();
        window.onafterprint = function () { window.close(); };
      };
    </script>
  </body>
</html>`);
  printWindow.document.close();
};
